import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Select } from "antd";
import classnames from "classnames";
import {
  setSelectedProjectIdForColumns,
  updateProjectColumns
} from "store/features/groupBy/groupBySlice";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { useProjectListQuery } from "components/project/projects/queries";

import { useGroupByStore } from "./GroupByStoreProvider";
import { selectEntityKind } from "./groupBySelectors";
import { useSelectedCategory } from "./hooks";

/* eslint-disable @typescript-eslint/no-explicit-any */
interface ProjectFieldToolbarProps {
  toolbarActive?: boolean;
  toolbarFocusedIndex?: Maybe<number>;
  onSelectRef?: (index: number, selectEl: Maybe<any>) => void;
}

function ProjectFieldToolbar({
  toolbarActive = false,
  toolbarFocusedIndex = null,
  onSelectRef
}: ProjectFieldToolbarProps = {}) {
  const dispatch = useDispatch();
  const { isProjectDefinedColumn } = useSelectedCategory();
  const entityKind = useGroupByStore(selectEntityKind);
  const selectedProjectId = useSelector(
    (state: RootState) => state.groupBy.selectedProjectIdForColumns
  );
  const isLoadingProjectColumns = useSelector(
    (state: RootState) => state.groupBy.isLoadingProjectColumns
  );

  const projectListQuery = useProjectListQuery({
    staleTime: 20000
  });

  // Create ref for the select component
  const selectRef = useRef<Maybe<any>>(null);

  // Report select refs to parent when they change
  useEffect(() => {
    if (onSelectRef) {
      onSelectRef(0, selectRef.current);
    }
  }, [onSelectRef, selectRef.current]);

  useEffect(() => {
    if (!isProjectDefinedColumn) return;

    if (!selectedProjectId && projectListQuery.data?.length > 0) {
      dispatch(setSelectedProjectIdForColumns(projectListQuery.data[0].projectId));
      return;
    }

    if (selectedProjectId) {
      dispatch(updateProjectColumns(entityKind));
    }
  }, [
    selectedProjectId,
    projectListQuery.data,
    dispatch,
    entityKind,
    isProjectDefinedColumn
  ]);

  const handleProjectChange = (projectId: string) => {
    if (isLoadingProjectColumns) return;
    dispatch(setSelectedProjectIdForColumns(projectId));
  };

  if (!isProjectDefinedColumn) return null;

  return (
    <Wrapper>
      <SelectContainer>
        <Select
          ref={(el) => {
            selectRef.current = el;
          }}
          className={classnames("toolbar-item", {
            "keyboard-focused": toolbarActive && toolbarFocusedIndex === 0
          })}
          data-testid="project-fields-select"
          value={selectedProjectId}
          onChange={handleProjectChange}
          loading={projectListQuery.isLoading || isLoadingProjectColumns}
          disabled={isLoadingProjectColumns}
          placeholder="Select Project"
          popupClassName="modal-select"
          options={projectListQuery.data?.map((project) => ({
            value: project.projectId,
            label: project.projectName
          }))}
        />
      </SelectContainer>
    </Wrapper>
  );
}

export default ProjectFieldToolbar;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  width: 100%;
  padding: 0 var(--space-4);
  margin-bottom: var(--space-1);
`;

const SelectContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  .ant-select {
    width: 100%;
  }
`;
